import { useState } from 'react';
import { Layers, Database, Lock, Plug, Rocket, Check, Plus, Sparkles, Twitch, MessageSquare, Calendar, Video, Shield, Zap, Package } from 'lucide-react';
import { synapseAPI } from '../lib/synapse-api';

// ============================================
// LIGHTSITE GENERATOR - Site Builder UI
// ============================================

interface BaseTemplate {
  id: string;
  name: string;
  description: string;
  stack: string[];
  features: Record<string, boolean>;
  files: number;
}

interface FeatureComponent {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  category: 'core' | 'integrations' | 'ui';
  files: string[];
}

const BASE_TEMPLATES: BaseTemplate[] = [
  {
    id: 'solaris-cyberpunk',
    name: 'Solaris Cyberpunk',
    description: 'Full finance education platform with cyberpunk theme, courses, quizzes, gamification',
    stack: ['React', 'TypeScript', 'Tailwind', 'Framer Motion', 'Vite'],
    features: { courses: true, quizzes: true, achievements: true, ai_coach: true, glossary: true, assessment: true },
    files: 27,
  },
  {
    id: 'streamer-site',
    name: 'Streamer Landing',
    description: 'Perfect for Twitch/YouTube streamers with schedule, clips, and social links',
    stack: ['React', 'TypeScript', 'Tailwind', 'Vite'],
    features: { auth: false, twitch: true, schedule: true, clips: true },
    files: 18,
  },
  {
    id: 'portfolio',
    name: 'Portfolio Site',
    description: 'Clean portfolio with projects, about, and contact sections',
    stack: ['React', 'TypeScript', 'Tailwind', 'Vite'],
    features: { auth: false, contact: true, gallery: true },
    files: 12,
  },
  {
    id: 'saas-landing',
    name: 'SaaS Landing',
    description: 'Conversion-focused landing page with pricing and features',
    stack: ['React', 'TypeScript', 'Tailwind', 'Vite'],
    features: { auth: true, api: true, stripe: true },
    files: 24,
  },
];

const FEATURE_COMPONENTS: FeatureComponent[] = [
  // Core Features
  { id: 'auth', name: 'Authentication', description: 'Login, register, OAuth (Twitch/Discord)', icon: Lock, color: 'text-green-400', category: 'core', files: ['authStore.ts', 'AuthModal.tsx', 'UserMenu.tsx'] },
  { id: 'database', name: 'Database', description: 'MySQL/PostgreSQL with migrations', icon: Database, color: 'text-blue-400', category: 'core', files: ['database.ts', 'migrations/'] },
  { id: 'api', name: 'API Layer', description: 'REST endpoints with validation', icon: Plug, color: 'text-yellow-400', category: 'core', files: ['api.ts', 'routes/'] },
  { id: 'websocket', name: 'WebSocket', description: 'Real-time updates and notifications', icon: Zap, color: 'text-purple-400', category: 'core', files: ['useWebSocket.ts', 'wsClient.ts'] },

  // Integrations
  { id: 'twitch', name: 'Twitch API', description: 'Live status, clips, schedule from Twitch', icon: Twitch, color: 'text-violet-400', category: 'integrations', files: ['useTwitch.ts', 'TwitchLiveIndicator.tsx', 'TwitchClips.tsx'] },
  { id: 'discord', name: 'Discord', description: 'Discord OAuth and server widgets', icon: MessageSquare, color: 'text-indigo-400', category: 'integrations', files: ['useDiscord.ts', 'DiscordWidget.tsx'] },
  { id: 'stripe', name: 'Stripe', description: 'Payments, subscriptions, checkout', icon: Shield, color: 'text-cyan-400', category: 'integrations', files: ['useStripe.ts', 'CheckoutButton.tsx'] },
  { id: 'ai_coach', name: 'AI Coach', description: 'OpenAI-powered chat assistant', icon: MessageSquare, color: 'text-emerald-400', category: 'integrations', files: ['AICoachChat.tsx'] },

  // UI Components
  { id: 'schedule', name: 'Schedule Cards', description: 'Weekly schedule with timezone support', icon: Calendar, color: 'text-orange-400', category: 'ui', files: ['Schedule.tsx', 'ScheduleCard.tsx'] },
  { id: 'clips', name: 'Clips Gallery', description: 'Video clips with modal player', icon: Video, color: 'text-pink-400', category: 'ui', files: ['Clips.tsx', 'ClipModal.tsx'] },
  { id: 'feedback', name: 'Feedback Widget', description: 'Client edit request widget', icon: MessageSquare, color: 'text-amber-400', category: 'ui', files: ['sitewarp-feedback.js'] },

  // Solaris Education Features
  { id: 'courses', name: 'Course System', description: 'Full course platform with lessons & progress', icon: Layers, color: 'text-pink-400', category: 'ui', files: ['CoursesPage.tsx', 'LessonCard.tsx', 'courses.ts'] },
  { id: 'quizzes', name: 'Quiz Library', description: '50+ quiz questions with categories', icon: Sparkles, color: 'text-purple-400', category: 'ui', files: ['QuizLibrary.tsx', 'QuizCard.tsx', 'quizQuestions.ts'] },
  { id: 'achievements', name: 'Achievement Badges', description: 'Gamification with 15+ badges', icon: Shield, color: 'text-amber-400', category: 'ui', files: ['AchievementBadges.tsx', 'StreakCounter.tsx', 'achievements.ts'] },
  { id: 'glossary', name: 'Glossary', description: 'A-Z searchable term definitions', icon: Database, color: 'text-blue-400', category: 'ui', files: ['Glossary.tsx', 'glossaryTerms.ts'] },
  { id: 'assessment', name: 'Wealth Assessment', description: '6-category financial assessment wizard', icon: Zap, color: 'text-cyan-400', category: 'ui', files: ['WealthAssessment.tsx', 'assessmentQuestions.ts'] },
  { id: 'scroll_reel', name: 'Scroll Reel', description: 'Epic horizontal scroll with particles & glitch', icon: Video, color: 'text-rose-400', category: 'ui', files: ['ScrollReel.tsx'] },
  { id: 'goal_tracker', name: 'Goal Tracker', description: 'Savings goals with progress rings', icon: Calendar, color: 'text-green-400', category: 'ui', files: ['GoalTracker.tsx'] },
  { id: 'tips', name: 'Daily Tips', description: '30+ rotating tips with challenges', icon: Sparkles, color: 'text-yellow-400', category: 'ui', files: ['TipOfDay.tsx', 'ChallengeCard.tsx', 'tips.ts'] },
];

export function LightSitePage() {
  const [selectedTemplate, setSelectedTemplate] = useState<BaseTemplate | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<Set<string>>(new Set());
  const [projectName, setProjectName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'templates' | 'components' | 'library'>('templates');
  const [libraryFiles, setLibraryFiles] = useState<{path: string, size: number}[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  // Fetch component library files from static manifest
  const loadLibrary = async () => {
    setLoadingLibrary(true);
    try {
      // Fetch manifest from shared/lightsite (symlinked to /shared/)
      const res = await fetch('/shared/lightsite/manifest.json');
      if (res.ok) {
        const data = await res.json();
        setLibraryFiles(data.files || []);
      }
    } catch (e) {
      console.error('Failed to load library:', e);
    } finally {
      setLoadingLibrary(false);
    }
  };

  const toggleFeature = (id: string) => {
    const newSet = new Set(selectedFeatures);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedFeatures(newSet);
  };

  const selectTemplate = (template: BaseTemplate) => {
    setSelectedTemplate(template);
    // Pre-select template's default features
    setSelectedFeatures(new Set(Object.entries(template.features).filter(([_, v]) => v).map(([k]) => k)));
  };

  const handleGenerate = async () => {
    if (!selectedTemplate || !projectName.trim()) return;
    setGenerating(true);

    try {
      const description = `Generate LightSite "${projectName}" from ${selectedTemplate.name} with features: ${Array.from(selectedFeatures).join(', ')}`;
      await synapseAPI.submitTask(description, 'NORMAL');

      setProjectName('');
      setSelectedTemplate(null);
      setSelectedFeatures(new Set());
      alert('Site generation started! Check Pipeline for progress.');
    } catch (err) {
      console.error('Failed to start generation:', err);
      alert('Failed to start generation. Check console for details.');
    } finally {
      setGenerating(false);
    }
  };

  const totalFiles = FEATURE_COMPONENTS.filter(f => selectedFeatures.has(f.id)).reduce((sum, f) => sum + f.files.length, 0) + (selectedTemplate?.files || 0);

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
          <Zap className="w-6 h-6 text-cyan-400" />
          LightSite Generator
        </h1>
        <p className="text-gray-400">Build sites from templates + pick your features</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'templates'
              ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
              : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:text-white'
          }`}
        >
          <Layers className="w-4 h-4 inline mr-2" />
          Templates
        </button>
        <button
          onClick={() => setActiveTab('components')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'components'
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
              : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:text-white'
          }`}
        >
          <Package className="w-4 h-4 inline mr-2" />
          Components ({selectedFeatures.size})
        </button>
        <button
          onClick={() => { setActiveTab('library'); loadLibrary(); }}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'library'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:text-white'
          }`}
        >
          <Database className="w-4 h-4 inline mr-2" />
          Library ({libraryFiles.length})
        </button>
      </div>

      {/* Generator Panel */}
      <div className="bg-[#161b22]/30 backdrop-blur-xl backdrop-saturate-150 border border-[#30363d] rounded-lg p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Template</label>
            <div className="px-3 py-2 bg-white/5/70 border border-[#30363d] rounded-lg text-white">
              {selectedTemplate?.name || <span className="text-gray-600">Select below...</span>}
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="my-site"
              className="w-full bg-white/5/70 border border-[#30363d] rounded-lg px-3 py-2 text-white focus:border-violet-500 focus:outline-none placeholder-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Features</label>
            <div className="px-3 py-2 bg-white/5/70 border border-[#30363d] rounded-lg text-cyan-400">
              {selectedFeatures.size} selected • {totalFiles} files
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleGenerate}
              disabled={!selectedTemplate || !projectName.trim() || generating}
              className="w-full px-4 py-2 bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg flex items-center justify-center gap-2 transition font-medium"
            >
              {generating ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Rocket className="w-4 h-4" />
              )}
              Generate
            </button>
          </div>
        </div>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {BASE_TEMPLATES.map((template) => (
            <div
              key={template.id}
              onClick={() => selectTemplate(template)}
              className={`bg-[#161b22]/30 backdrop-blur-xl backdrop-saturate-150 border rounded-lg p-5 cursor-pointer transition group ${
                selectedTemplate?.id === template.id
                  ? 'border-violet-500/50 ring-1 ring-violet-500/30'
                  : 'border-[#30363d] hover:border-violet-500/30'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-white group-hover:text-violet-400 transition">
                  {template.name}
                </h3>
                {selectedTemplate?.id === template.id && <Check className="w-5 h-5 text-violet-400" />}
              </div>
              <p className="text-sm text-gray-400 mb-4">{template.description}</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {template.stack.map((tech) => (
                  <span key={tech} className="px-2 py-0.5 bg-white/5/70 rounded text-xs text-gray-300">{tech}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{template.files} files</span>
                <span>•</span>
                <span>{Object.values(template.features).filter(Boolean).length} features</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Components Tab */}
      {activeTab === 'components' && (
        <div className="space-y-6">
          {(['core', 'integrations', 'ui'] as const).map((category) => (
            <div key={category}>
              <h3 className="text-sm font-medium text-gray-400 uppercase mb-3">
                {category === 'core' ? 'Core Features' : category === 'integrations' ? 'Integrations' : 'UI Components'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {FEATURE_COMPONENTS.filter(f => f.category === category).map((feature) => {
                  const Icon = feature.icon;
                  const isSelected = selectedFeatures.has(feature.id);
                  return (
                    <div
                      key={feature.id}
                      onClick={() => toggleFeature(feature.id)}
                      className={`bg-[#161b22]/30 backdrop-blur border rounded-lg p-4 cursor-pointer transition ${
                        isSelected
                          ? 'border-cyan-500/50 bg-cyan-500/10'
                          : 'border-[#30363d] hover:border-[#30363d]/80'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-cyan-500/20' : 'bg-white/5'}`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-cyan-400' : feature.color}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-white">{feature.name}</h4>
                            {isSelected && <Check className="w-4 h-4 text-cyan-400" />}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{feature.description}</p>
                          <p className="text-xs text-gray-600 mt-2">{feature.files.length} files</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Library Tab - Browse Generated Components */}
      {activeTab === 'library' && (
        <div className="space-y-4">
          {loadingLibrary ? (
            <div className="text-center py-12 text-gray-500">
              <div className="w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              Loading component library...
            </div>
          ) : libraryFiles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No components in library yet.</p>
              <p className="text-sm mt-2">Generate templates or submit component tasks to populate.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400">{libraryFiles.length} files in /shared/lightsite/</p>
                <button
                  onClick={loadLibrary}
                  className="px-3 py-1 text-sm bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition"
                >
                  Refresh
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {libraryFiles.map((file, i) => {
                  const name = file.path.split('/').pop() || file.path;
                  const folder = file.path.replace(name, '').replace(/\/$/, '').split('/').pop() || '';
                  const ext = name.split('.').pop() || '';
                  const extColor = ext === 'tsx' ? 'text-blue-400' : ext === 'ts' ? 'text-yellow-400' : 'text-gray-400';
                  return (
                    <div
                      key={i}
                      className="bg-[#161b22]/30 backdrop-blur border border-[#30363d] rounded-lg p-3 hover:border-green-500/30 transition"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-mono ${extColor}`}>.{ext}</span>
                        <span className="text-xs text-gray-600">{folder}/</span>
                      </div>
                      <p className="text-sm text-white font-medium truncate">{name}</p>
                      <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </main>
  );
}
